import { useEffect, useRef, useState, useCallback } from "react";
import _, { get, set } from "lodash-es";
import { Form } from "@formio/react";
import { Alert, Button, Flex, Space, Spin } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import intl from "react-intl-universal";
import request from "../../../libs/request";
import { isBase64Url, uploadFile, imageUrlToBase64 } from "../../../libs/FileHelper";
import Evisit from "../../../libs/Evisit";

const QUEUE_ELIGIBILITY_SOURCE = {
    "primary": "primary",
    "secondary": "secondary",
};

const HAS_INSURANCE_OPTIONS = {
    "yes": "yes",
    "no": "no",
}

const useEligibilityStatus = (patientId, queueEligibilitySource, workflowData) => {
    const [loading, setLoading] = useState(false);
    const [insurance, setInsurance] = useState({});
    const [error, setError] = useState(null);
    const [trigger, setTrigger] = useState(0);
    const [cacheTimestamp, setCacheTimestamp] = useState(null);
    const cacheKey = `${patientId}-${queueEligibilitySource}`;
    const prefillData = get(workflowData, "insurance.value");
    const userInfoData = get(workflowData, "user_info.value");

    // Cache to store responses
    const cache = useRef(new Map());

    const fetchEligibilityStatus = useCallback(async () => {
        if (cache.current.has(cacheKey)) {
            setInsurance(cache.current.get(cacheKey).data);
            setCacheTimestamp(cache.current.get(cacheKey).timestamp);
            return;
        }

        if (prefillData) {
            setInsurance(prefillData);
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${window.base_url}/api_v3/users/${patientId}/insurances/${queueEligibilitySource}`, {
                method: "GET",
                headers: {
                    "X-ApiToken": window.user.token,
                    "X-AccountCode": window.CONFIG.Account.code
                },
                credentials: "omit"
            });
            if (!response.ok) {
                throw new Error(`Error: ${response.statusText}`);
            }
            const { data } = await response.json();
            const insurance = await prepareInsuranceData(data, userInfoData);
            setInsurance(insurance || {});
            const timestamp = new Date().toISOString();
            cache.current.set(cacheKey, { data: insurance || {}, timestamp });
            setCacheTimestamp(timestamp);
        } catch (e) {
            console.error(e);
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [patientId, queueEligibilitySource, cacheKey]);

    useEffect(() => {
        fetchEligibilityStatus();
    }, [fetchEligibilityStatus, trigger]);

    const refetch = () => setTrigger(prev => prev + 1);

    return { 
        loading, 
        insurance, 
        error, 
        refetch, 
        cacheKey: cacheKey, 
        cacheTimestamp,
        trigger
    };
};

async function prepareInsuranceData(insurance, userInfoData) {
    if (insurance.card_front) {
        set(insurance, "__card_front", [{
            ...insurance.card_front,
            url: await imageUrlToBase64(insurance.card_front.fullpath),
            storage: "base64",
            type: "image/" + insurance.card_front.ext,
        }]);
    } else {
        set(insurance, "__card_front", []);
    }

    if (insurance.card_back) {
        set(insurance, "__card_back", [{
            ...insurance.card_back,
            url: await imageUrlToBase64(insurance.card_back.fullpath),
            storage: "base64",
            type: "image/" + insurance.card_back.ext,
        }]);
    } else {
        set(insurance, "__card_back", []);
    }
    // prefill some fields from user info if user dont have insurance data
    if (!insurance.carrier_code && userInfoData) {
        const fields = [
            ['guarantor_state', 'state'],
            ['guarantor_gender', 'gender'],
            ['guarantor_dob', 'dob'],
            ['guarantor_address', 'street_addr'],
            ['guarantor_first_name', 'first_name'],
            ['guarantor_last_name', 'last_name'],
            ['guarantor_zip', 'zip'],
            ['guarantor_city', 'city'],
          ];
          
          fields.forEach(([insuranceField, userField]) => {
            set(insurance, insuranceField, userInfoData?.[userField]);
          });
    }
    // some select fields in formio not consistent with old data, mapping them to correct format with another fields
    insurance.carrier_code && set(insurance, "_carrier_code", {id: insurance.carrier_code});
    insurance.guarantor_state && set(insurance, "_guarantor_state", {id: insurance.guarantor_state});
    insurance.respparty && set(insurance, "_respparty", {id: insurance.respparty});

    return insurance;
}

function Insurance(props) {
    const componentKey = "insurance";
    const { 
        currentComponent, 
        onNext, 
        onPrevious, 
        data, 
        isMobileWebview 
    } = props;
    const [formIOComponents, setFormIOComponents] = useState({components: []});
    const [loading, setLoading] = useState(false);
    const [nextBtnText, setNextBtnText] = useState(intl.get("continue").d("Continue"));
    const [message, setMessage] = useState(null);
    const [queueEligibilitySource, setQueueEligibilitySource] = useState(QUEUE_ELIGIBILITY_SOURCE['primary']);
    const config = {
        eligibilityCheckDisabled: Evisit.c('Account.claims.eligibility_check.disabled'),
        checkEligibilityDelay: Evisit.c('Account.claims.default.eligibility_check_delay', 10),
        proceedAfterEligibilityCheck: Evisit.c('Account.claims.proceed_after_eligibility_check'),
    }
    const formRef = useRef();
    const workflowData = _.get(data, `${componentKey}.value`);
    const {
        insurance,
        refetch: refetchEligibilityStatus,
        loading: loadingEligibilityStatus,
        trigger,
    } = useEligibilityStatus(getPatientId(), queueEligibilitySource, data);

    useEffect(() => {
        const components = _.get(currentComponent, "input.form.components", []);
        if (components) {
            setFormIOComponents({components: components});
        }
    }, [currentComponent]);

    async function onSubmit(submission) {
        setLoading(true);
        setMessage(null);
        if (submission.data && get(submission.data, "has_ins") === HAS_INSURANCE_OPTIONS["yes"]) {
            setNextBtnText(intl.get("uploading_insurance_card").d("Uploading Insurance Card..."));

            if (get(submission.data, "__card_front.length", 0) > 0) {
                const cardFront = get(submission.data, "__card_front[0]");
                if (isBase64Url(cardFront.url)) {
                    const resp = await uploadFile(cardFront.url, cardFront.name)
                    if (!resp) {
                        raise({
                            message: intl.get("error_uploading_card_front").d("Error uploading card front"),
                            type: "error",
                        });
                        return;
                    }
                    submission.data.card_front = resp.id;
                }
            }
        
            if (get(submission.data, "__card_back.length", 0) > 0) {
                const cardBack = get(submission.data, "__card_back[0]");
                if (isBase64Url(cardBack.url)) {
                    const resp = await uploadFile(cardBack.url, cardBack.name);
                    if (!resp) {
                        raise({
                            message: intl.get("error_uploading_card_back").d("Error uploading card back"),
                            type: "error",
                        });
                        return;
                    }
                    submission.data.card_back = resp.id;
                }
            }

            if (get(submission.data, "_carrier_code")) {
                submission.data.carrier_code = get(submission.data, "_carrier_code.id");
            }

            if (get(submission.data, "_guarantor_state")) {
                submission.data.guarantor_state = get(submission.data, "_guarantor_state.id");
            }

            if (get(submission.data, "_respparty")) {
                submission.data.respparty = get(submission.data, "_respparty.id");
            }

            if (!config.eligibilityCheckDisabled) {
                return await checkEligibility(submission.data);
            }
            
            await submitCollectInsuranceData(submission.data);
        }
        setNextBtnText(intl.get("continue").d("Continue"));
        onNext(submission.data);
    }

    async function submitCollectInsuranceData(postData) {
        const patientId = getPatientId();
        const payload = getSubmitPayload(postData);
        setNextBtnText(intl.get("please_wait").d("Please wait..."));
        try {
            await request.post(`${window.base_url}/api_v3/users/${patientId}/insurances/${queueEligibilitySource}`, payload)
        } catch (e) {
            raise({
                message: intl.get("error_submitting_insurance_data").d("Error submitting insurance data"),
                type: "error",
            });
            return;
        }
    }
    
    async function checkEligibility(postData) {
        const patientId = getPatientId();
        const payload = getSubmitPayload(postData);
        setNextBtnText(intl.get("checking_eligibility").d("Checking eligibility..."));
        try {
            await request.post(`${window.base_url}/api_v3/users/${patientId}/insurances/${queueEligibilitySource}/eligibility/check`, payload);
            // Wait for 10 seconds before calling getEligibilityStatus
            setTimeout(async () => {
                await getEligibilityStatus(payload);
            }, config.checkEligibilityDelay * 1000);
            return;
        } catch (e) {
            raise({
                message: intl.get("error_checking_eligibility").d("Error checking eligibility"),
                type: "error",
            });
            return;
        }
    }

    async function getEligibilityStatus(postData) {
        const patientId = getPatientId();
        try {
            const response = await request.get(`${window.base_url}/api_v3/users/${patientId}/insurances/${queueEligibilitySource}`);
            const insurance = response.data || {};
            const currentEligibilityStatus = get(insurance, 'eligibility.status');
            const eligibilityStatusText = get(insurance, 'eligibility.status_text');
            
            if(canProcessAfterEligibilityCheck(currentEligibilityStatus)) {
                setNextBtnText(intl.get("continue").d("Continue"));
                setLoading(false);
                onNext(postData);
                return;
            }

            if (eligibilityStatusText) {
                raise({
                    message: eligibilityStatusText || intl.get("error_getting_eligibility_status").d("Error getting eligibility status"),
                    type: get(insurance, 'eligibility.type', "error"),
                });
            }

        } catch (e) {
            raise({
                message: intl.get("error_getting_eligibility_status").d("Error getting eligibility status"),
                type: "error",
            });
            return;
        }
    }

    function canProcessAfterEligibilityCheck(status) {
        const mappingStatus = {
            'active': window.CONST.ELIGIBILITY.STATUS.ACTIVE,
            'inactive': window.CONST.ELIGIBILITY.STATUS.INACTIVE,
            'pending': window.CONST.ELIGIBILITY.STATUS.PENDING,
            'failed': window.CONST.ELIGIBILITY.STATUS.FAILED,
            'expired': window.CONST.ELIGIBILITY.STATUS.EXPIRED,
            'unknown': window.CONST.ELIGIBILITY.STATUS.UNKNOWN,
        }
        const mappedStatus = mappingStatus[status] || mappingStatus.unknown;
        return config.proceedAfterEligibilityCheck.includes(mappedStatus);

    }

    function raise({message, type}) {
        setNextBtnText(intl.get("continue").d("Continue"));
        setLoading(false);
        setMessage({
            text: message,
            type: type || "error",
        });
    }

    function getPatientId() {
        if (data?.user_info) {
            return get(data, "user_info.value.id", window.user.id);
        }
        return window.user.id;
    }

    function getSubmitPayload(postData) {
        const payload = _.cloneDeep(postData);
        const shouleRemoveKeys = [
            "queueing", "parsed_response", "eligibility"
        ]
        Object.keys(payload).forEach(key => {
            if (key.startsWith('__') || shouleRemoveKeys.includes(key)) {
              delete payload[key];
            }
        });
        return payload;
    }

    useEffect(() => {
        async function updateSubmission() {
            if (formRef.current) {
                formRef.current.submission = {
                    data: {
                        ...(workflowData ? workflowData : insurance), 
                        has_ins: get(formRef.current, "submission.data.has_ins") || HAS_INSURANCE_OPTIONS["no"]
                    }
                };
            }
        };
    
        updateSubmission();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [workflowData, insurance, trigger]);

    function handleFormOnchange (submission) {
        if (get(submission, "changed.component.key") === "has_ins") {
            refetchEligibilityStatus();
        }
    }

    function handleOnGoBack() {
        // as QA requirement, at least keep data in the form insurance when user go back
        // for now, only apply for this component, limit impact to other components
        // should apply for all components later
        if (onPrevious) {
            onPrevious({formData: _.get(formRef.current, "submission.data")});
        }
    }

    return (
        <>
            <Space direction="vertical" size="middle" style={{ display: 'flex' }}>
                <Spin spinning={loadingEligibilityStatus}>
                    <Form
                        form={formIOComponents}
                        onSubmit={(submission) => onSubmit(submission)}
                        formReady={(formInstance) => {
                            formRef.current = formInstance;
                            formInstance.submission = {
                                data: (workflowData ? workflowData : insurance)
                            };
                        }}
                        onChange={handleFormOnchange}
                        options={{
                            noAlerts: true,
                        }}
                    />
                </Spin>
                {
                    message && <Alert type={message.type} message={message.text} />
                }
                <Flex justify={isMobileWebview ? "center" : "space-between"}>
                    {(onPrevious != null && !isMobileWebview) ?
                        <Button onClick={() => handleOnGoBack()}>
                            <LeftOutlined style={{ verticalAlign: "middle" }} />
                            {intl.get("back").d("Back")}
                        </Button> : <div></div>
                    }
                    <Button
                        type="primary"
                        loading={loading}
                        shape={isMobileWebview ? "round" : "default"}
                        size={isMobileWebview ? "large" : "default"}
                        onClick={() => {
                            formRef.current.submit();
                        }}>
                        {nextBtnText}
                        <RightOutlined style={{ verticalAlign: "middle" }} />
                    </Button>
                </Flex>
            </Space>
        </>
    );
}

export default Insurance;